import { FC, useEffect, useState } from "react";
import "./ModalArticlesInsert.scss";
import ModalDefault from "../../../components/ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Card,
  Col,
  FormCheck,
  FormGroup,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import { useArticles } from "../../../providers/Articles/ArticlesProvider";
import ArticleEditor from "../ArticleEditor/ArticleEditor";
import ReactSelect from "react-select";
import { useAgentsCategories } from "../../../providers/Agents/AgentsCategories/AgentsCategoriesProvider";
import { customStyles } from "../../../models/SelectCustomStyles";
import { useSections } from "../../../providers/Articles/Sections/SectionsProvider";
import { useSubsections } from "../../../providers/Articles/Subsections/SubsectionsProvider";
import InputDropzone from "../../InputDropzone/InputDropzone";
import { ArticlesModel } from "../../../models/Articles/ArticleModel";
import getFileParts from "../../../utils/GetFileParts";
import { BvIcon } from "bevi-icon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { useAccessProfile } from "../../../providers/Access/AccessProfile/AccessProfileProvider";
import { useArticleLock } from "../../../providers/Articles/ArticlesLocks/ArticlesLocksProvider";
import { useTag } from "../../../providers/Tags/TagsProvider";

interface ModalArticlesInsertProps {
  show: boolean;
  article_edit?: ArticlesModel | null;
  article_clone?: ArticlesModel | null;
  onClose: () => void;
}

type Processed = {
  id: number;
  value: any;
};

type ExcelFileTab = {
  id: number;
  file?: File;
  filePath?: string;
  area?: string;
};

type FormValues = {
  id?: number;
  title?: string;
  content?: string;
  content_ia?: string;
  category_id?: number;
  section_id?: number;
  subsection_id?: number;
  prompt_article?: string;
  model_name?: string;
  list_prompt?: string[];
  is_file?: boolean;
  is_internal?: boolean;
  related_articles?: number[];
  access_profile_articles?: number[];
  tags?: number[];
};

const ModalArticlesInsert: FC<ModalArticlesInsertProps> = ({
  show,
  onClose,
  article_edit,
  article_clone,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<any | null>(
    null,
  );
  const [selectedSection, setSelectedSection] = useState<any | null>(null);
  const [selectedSubsection, setSelectedSubsection] = useState<any | null>(null);
  const [selectedRelatedArticles, setSelectedRelatedArticles] = useState<any[]>([]);
  const [selectedAccessProfileArticles, setSelectedAccessProfileArticles] = useState<any[]>([]);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [cloneSource, setCloneSource] = useState<ArticlesModel | null>(null);
  const [selectedIsFile, setSelectedIsFile] = useState<boolean>(false);
  const [selectedIsInternal, setSelectedIsInternal] = useState<boolean>(false);
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [selectedEditorTab, setSelectedEditorTab] = useState<"default" | "ia">(
    "default",
  );
  const [files, setFiles] = useState<any[]>([]);
  const [fileTypes, setFileTypes] = useState<any>({
    "application/vnd.ms-excel": [".xlsx"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
  });
  const [modelNameOptions] = useState<any | null>([
    { value: "gpt-3.5-turbo", label: "gpt-3.5-turbo" },
    { value: "gpt-4o", label: "gpt-4o" },
    { value: "gpt-4o-mini", label: "gpt-4o-mini" },
    { value: "gpt-4o-nano", label: "gpt-4o-nano" },
    { value: "gpt-4.1", label: "gpt-4.1" },
    { value: "gpt-4.1-mini", label: "gpt-4.1-mini" },
    { value: "gpt-5", label: "gpt-5" },
    { value: "gpt-5-mini", label: "gpt-5-mini" },
    { value: "gpt-5-nano", label: "gpt-5-nano" },
  ]);
  const MAX_PROMPTS = 13;
  const MAX_EXCEL = 5;
  const [prompts, setPrompts] = useState<Processed[]>([{ id: 1, value: "" }]);
  const [activePromptId, setActivePromptId] = useState<number | null>(1);
  const activePrompt = prompts.find((prompt) => prompt.id === activePromptId);
  const [selectedModelName, setSelectedModelName] = useState<any | null>(null);
  const [filesExcel, setFilesExcel] = useState<ExcelFileTab[]>([{ id: 1 }]);
  const [activeFileId, setActiveFileId] = useState<number | null>(1);
  const activeFile = filesExcel.find((f) => f.id === activeFileId);
  const fileStatus = !activeFile
    ? "new"
    : activeFile.file
      ? "replacing"
      : activeFile.filePath && editingFileId !== activeFile.id
        ? "existing"
        : "replacing";

  const { categoriesOptions, setParams: setParamsAgentsCategories } =
    useAgentsCategories();
  const { sectionsOptions, setParams: setParamsSections } = useSections();
  const { subsectionsOptions, setParams: setParamsSubsections } =
    useSubsections();
  const { accessProfilesOptions, setParams: setParamsAccessProfiles } =
    useAccessProfile();
  const {
    setArticle,
    article,
    setError,
    handleList,
    handleFetch,
    setParamsArticlesRelated,
    articlesRelatedOptions,
    isPreview,
    setIsPreview,
  } = useArticles();
  const { tagsOptions, setParams: setParamsTags } = useTag();

  const { onInvalid } = useAuth();
  const { handleFetch: handleFetchArticleLock } = useArticleLock();

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(schema),
  });

  const handleCloseSetup = () => {
    setError("");
    setValue("title", "");
    setValue("content", "");
    setValue("content_ia", "");
    setValue("prompt_article", "");
    setSelectedIsFile(false);
    setSelectedIsInternal(false);
    setSelectedCategories(null);
    setSelectedSection(null);
    setSelectedSubsection(null);
    setEditingFileId(null);
    setIsPreview(false);
    setSelectedRelatedArticles([]);
    setSelectedAccessProfileArticles([]);
    setSelectedTags([]);
    setSelectedEditorTab("default");
    setValue("list_prompt", []);
    setValue("list_file", []);
    setPrompts([{ id: 1, value: "" }]);
    setFilesExcel([{ id: 1 }]);
    setActivePromptId(1);
    setActiveFileId(1);
    setValue("model_name", "");
    setSelectedModelName(null);
    setCloneSource(null);
    onClose();
  };

  const handleClose = () => {
    if(article_edit?.id) {
      handleFetchArticleLock({ article_id: article_edit?.id ?? null, status: 0 }).then(() => {
        handleList();
        handleCloseSetup();
      });
    } else {
      handleList();
      handleCloseSetup();
    }
  };

  const onSubmit = async (data: any) => {
    setIsLoadingSubmit(true);
    setError("");

    const ret = await handleFetch(article_edit?.id ?? null, data as any);

    if (ret) {
      toast.success("Artigo salvo com sucesso!");
      handleClose();
    }

    setIsLoadingSubmit(false);
  };

  const applyClone = (source: ArticlesModel) => {
    setValue("title", `(Clone) ${source?.title ?? ""}`, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("content", source?.content ?? "", {
      shouldValidate: !!source?.content,
    });
    setValue("content_ia", source?.content_ia ?? "", {
      shouldValidate: !!source?.content_ia,
    });

    setSelectedModelName(
      modelNameOptions
        .filter((item: any) => item.value === source?.model_name)
        ?.shift() || null,
    );

    const isFileSource =
      typeof source?.is_file === "boolean" ? source.is_file : false;
    setSelectedIsFile(isFileSource);

    if (isFileSource) {
      const mapped = source?.list_prompt?.map((x: any) => ({
        id: x.id ?? Date.now(),
        value: x.prompts_articles,
      })) ?? [{ id: Date.now(), value: "" }];

      setPrompts(mapped);
      setActivePromptId(mapped[0]?.id ?? 1);
      setValue("prompt_article", "");
    } else {
      setValue("prompt_article", source?.prompt_article ?? "");
    }

    setCloneSource(source);
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setIsPreview(false);
    }
  }, [errors]);

  useEffect(() => {
    if (show) {
      setParamsAgentsCategories({
        withPaginate: false,
        filter: { only_active: true },
      });
      setParamsAccessProfiles({
        withPaginate: false,
        filter: { only_active: true },
      });
      setParamsTags({
        withPaginate: false,
        filter: { only_active: true },
      });
      setEditingFileId(null);
      setSelectedEditorTab("default");

      if (article_edit?.id) {
        reset(article_edit as FormValues);
        setValue("title", article_edit?.title ? article_edit?.title : "");
        setValue("content", article_edit?.content ?? "", {
          shouldValidate: !!article_edit?.content,
        });
        setValue("content_ia", article_edit?.content_ia ?? "", {
          shouldValidate: !!article_edit?.content_ia,
        });
        setValue(
          "category_id",
          article_edit?.category?.id ? article_edit?.category?.id : 0,
        );
        setValue(
          "section_id",
          article_edit?.section?.id ? article_edit?.section?.id : 0,
        );
        setValue(
          "subsection_id",
          article_edit?.subsection?.id ? article_edit?.subsection?.id : 0,
        );
        setValue(
          "related_articles",
          article_edit?.related_articles?.map(
            (item) => item?.related_article?.id,
          ) || [],
        );
        setValue(
          "access_profile_articles",
          article_edit?.access_profile_articles?.map((item: number) => item) ||
            [],
        );

        if (typeof article_edit?.is_file === "boolean") {
          setSelectedIsFile(article_edit?.is_file);
        }
        if (typeof article_edit?.is_internal === "boolean") {
          setSelectedIsInternal(article_edit?.is_internal);
        }

        article_edit?.category?.id &&
          setSelectedCategories({
            value: article_edit?.category?.id,
            label: article_edit?.category?.name,
          });
        article_edit?.section?.id &&
          setSelectedSection({
            value: article_edit?.section?.id,
            label: article_edit?.section?.name,
          });
        article_edit?.subsection?.id &&
          setSelectedSubsection({
            value: article_edit?.subsection?.id,
            label: article_edit?.subsection?.name,
          });
        setPrompts(
          article_edit?.list_prompt?.map((x: any) => ({
            id: x.id,
            value: x.prompts_articles,
          })) ?? [{ id: Date.now(), value: "" }],
        );

        setFilesExcel(
          article_edit?.list_file?.map((x: any) => ({
            id: x.id,
            file: undefined,
            filePath: x?.filename,
            area: x?.area,
          })) ?? [{ id: Date.now() }],
        );

        if (typeof article_edit?.is_file === "boolean") {
          article_edit?.list_prompt?.length &&
            setActivePromptId(article_edit?.list_prompt[0]?.id ?? 1);
          article_edit?.list_file?.length &&
            setActiveFileId(article_edit?.list_file?.[0]?.id ?? 1);
        }
        setSelectedModelName(
          modelNameOptions
            .filter((item: any) => item.value === article_edit?.model_name)
            ?.shift() || null,
        );
      } else {
        article_edit = null;
        reset({} as FormValues);
        setValue("title", "");
        setValue("content", "");
        setValue("content_ia", "");
        setValue("model_name", "");
        setValue("prompt_article", "");
        setValue("category_id", 0);
        setValue("is_internal", false);
        setSelectedModelName(null);
        setSelectedCategories(null);
        setSelectedSection(null);
        setSelectedSubsection(null);
        setSelectedIsFile(false);
        setSelectedIsInternal(false);
        setSelectedRelatedArticles([]);
        setSelectedAccessProfileArticles([]);
        setSelectedTags([]);
        setCloneSource(null);

        if (article_clone?.id) {
          applyClone(article_clone);
        }
      }
    }
  }, [show]);

  useEffect(() => {
    setValue("category_id", selectedCategories?.value || null);
    if (!!selectedCategories) {
      setParamsSections({
        withPaginate: false,
        filter: { only_active: true, category_id: selectedCategories?.value },
      });
      setParamsArticlesRelated({
        withPaginate: false,
        filter: { only_active: true, category_id: selectedCategories?.value },
      });
    }
  }, [selectedCategories]);

  useEffect(() => {
    setValue("section_id", selectedSection?.value || null);
    !!selectedSection &&
      setParamsSubsections({
        withPaginate: false,
        filter: { only_active: true, section_id: selectedSection?.value },
      });
  }, [selectedSection]);

  useEffect(() => {
    setValue("subsection_id", selectedSubsection?.value || null);
  }, [selectedSubsection]);

  useEffect(() => {
    if (article_edit?.id && categoriesOptions?.length > 0) {
      setSelectedCategories(
        categoriesOptions
          .filter((item: any) => item.value === article_edit?.category?.id)
          ?.shift(),
      );
    }
  }, [categoriesOptions, article_edit]);

  useEffect(() => {
    if (article_edit?.section?.id && sectionsOptions?.length > 0) {
      setSelectedSection(
        sectionsOptions
          .filter((item: any) => item.value === article_edit?.section?.id)
          ?.shift(),
      );
    }
  }, [sectionsOptions, article_edit]);

  useEffect(() => {
    if (article_edit?.subsection?.id && subsectionsOptions?.length > 0) {
      setSelectedSubsection(
        subsectionsOptions
          .filter((item: any) => item.value === article_edit?.subsection?.id)
          ?.shift(),
      );
    }
  }, [subsectionsOptions, article_edit]);

  useEffect(() => {
    setValue(
      "related_articles",
      selectedRelatedArticles?.map((item) => item.value) || [],
    );
  }, [selectedRelatedArticles]);

  useEffect(() => {
    setSelectedRelatedArticles(
      article_edit?.related_articles?.map((item) => ({
        label:
          item?.related_article?.category?.name +
          " - " +
          item?.related_article?.title,
        value: item?.related_article?.id,
      })) || [],
    );
  }, [article_edit]);
  
  useEffect(() => {
    setValue(
      "tags",
      selectedTags?.map((item) => item.value) || []
    );
  }, [selectedTags]);

  useEffect(() => {
    setSelectedTags(
      article_edit?.tags?.map((item) => ({
        label: item?.content,
        value: item?.id,
      })) || []
    );
  }, [article_edit]);

  useEffect(() => {
    const selectedIds = article_edit?.access_profile_articles ?? [];

    setSelectedAccessProfileArticles(
      (accessProfilesOptions ?? [])
        .filter((item) => selectedIds.includes(item.value))
        .map((item) => ({
          label: item.label,
          value: item.value,
        })),
    );
  }, [accessProfilesOptions, article_edit?.access_profile_articles]);

  useEffect(() => {
    setValue(
      "access_profile_articles",
      selectedAccessProfileArticles?.map((item) => item.value) || [],
    );
  }, [selectedAccessProfileArticles]);

  useEffect(() => {
    if (files) {
      setValue("list_file", files[0] || null, { shouldValidate: !!files[0] });
    }
  }, [files]);

  useEffect(() => {
    setValue("is_file", selectedIsFile);
  }, [selectedIsFile]);

  useEffect(() => {
    setValue("is_internal", selectedIsInternal);
  }, [selectedIsInternal]);

  useEffect(() => {
    setValue("model_name", selectedModelName?.value || null);
  }, [selectedModelName]);

  useEffect(() => {
    const values: string[] = prompts
      .map((prompt) => prompt.value)
      .filter(Boolean);

    setValue("list_prompt", values);
  }, [prompts, setValue]);

  useEffect(() => {
    if (!filesExcel || filesExcel.length === 0) return;

    setValue("list_file", filesExcel, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [filesExcel, setValue]);

  const changeIsFile = (action: boolean) => {
    setSelectedIsFile(action);
    setValue("is_file", action);
  };

  const changeIsInternal = (action: boolean) => {
    setSelectedIsInternal(action);
    setValue("is_internal", action);
  };

  const addPrompt = () => {
    if (prompts.length >= MAX_PROMPTS) return;

    setPrompts((prev) => [
      ...prev,
      { id: Date.now(), value: "", label: "", icon: faCirclePlus },
    ]);

    setActivePromptId(Date.now());
  };

  const addFiles = () => {
    if (filesExcel.length >= MAX_EXCEL) return;

    const maxIndex = filesExcel.reduce((max, item) => {
      return Math.max(max, getAreaIndex(item.area));
    }, 0);

    const nextIndex = maxIndex + 1;

    const newItem = {
      id: Date.now(),
      area: `${article_edit?.id}-${nextIndex}`,
      file: undefined,
      filePath: undefined,
    };

    setFilesExcel((prev) => [...prev, newItem]);
    setActiveFileId(newItem.id);
  };

  const removePrompt = (promptId: number) => {
    setPrompts((prev) => {
      const newPrompts = prev.filter((prompt) => prompt.id !== promptId);

      if (newPrompts.length === 0) {
        const newPrompt = { id: Date.now(), value: "" };
        setActivePromptId(newPrompt.id);
        return [newPrompt];
      }

      if (promptId === activePromptId) {
        setActivePromptId(newPrompts[0].id);
      }

      return newPrompts;
    });
  };

  const handleInformationPrompt = (id: number, value: string) => {
    setPrompts((prev) =>
      prev.map((prompt) => (prompt.id === id ? { ...prompt, value } : prompt)),
    );
  };

  const updateActiveFile = (file: File) => {
    setFilesExcel((prev) =>
      prev.map((item) =>
        item.id === activeFileId
          ? {
              ...item,
              file,
              filePath: undefined,
            }
          : item,
      ),
    );
  };

  const removeFileTab = (fileId: number) => {
    setFilesExcel((prev) => {
      const remaining = prev.filter((file) => file.id !== fileId);

      if (remaining.length === 0) {
        const newFile = {
          id: Date.now(),
          area: undefined,
          file: undefined,
          filePath: undefined,
        };

        setActiveFileId(newFile.id);
        return [newFile];
      }

      if (fileId === activeFileId) {
        const sorted = [...remaining].sort(
          (a, b) => getAreaIndex(a.area) - getAreaIndex(b.area),
        );

        setActiveFileId(sorted[0].id);
      }

      return remaining;
    });
  };

  const getAreaIndex = (area?: string) => {
    if (!area) return 0;
    const match = area.match(/-(\d+)$/);
    return match ? Number(match[1]) : 0;
  };

  return (
    <ModalDefault
      title={article_edit?.id ? "Editar Artigo" : "Adicionar Artigo"}
      show={show}
      onClose={handleClose}
      sizeModal={"xxl"}
      showFooter={true}
      buttonText={isLoadingSubmit ? "Salvando..." : "Salvar"}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div className="ModalArticlesInsert" data-testid="ModalArticlesInsert">
        <Row>
          <Col md={8}>
            <Row>
              <Col md={12}>
                <FormGroup className="form-group">
                  <input
                    placeholder="Digite aqui o título do artigo"
                    className={`form-control input ${onInvalid(
                      errors?.title,
                    )} ${errors?.title ? "input-error" : ""}`}
                    type="text"
                    {...register("title")}
                    onChange={(e: any) =>
                      setArticle((prev) => ({
                        ...prev,
                        title: e?.target?.value,
                      }))
                    }
                  />
                  <ErrorMessage
                    name={"Título"}
                    type={errors?.title?.type?.toString()}
                    min={3}
                    max={255}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row className="h-100">
              <Col className="height-editor">
                <Tabs
                  id="controlled-tab-example"
                  activeKey={selectedEditorTab}
                  onSelect={(k: any) => setSelectedEditorTab(k)}
                >
                  <Tab
                    eventKey="default"
                    title={`Conteúdo Usuário *`}
                    tabClassName={`${onInvalid(errors?.content)}`}
                  >
                    <ArticleEditor
                      initialHtml={article_edit?.content ?? cloneSource?.content}
                      onChange={(value) => {
                        setArticle((prev) => ({
                          ...prev,
                          content: value.html,
                        }));
                        setValue("content", value.html ?? "", {
                          shouldDirty: true,
                        });
                      }}
                      className={`${onInvalid(errors?.content)}`}
                      register={register}
                    />
                    <ErrorMessage
                      name={"Conteúdo"}
                      type={errors?.content?.type?.toString()}
                      min={3}
                    />
                  </Tab>
                  <Tab
                    eventKey="ia"
                    title={`Conteúdo IA`}
                    tabClassName={`${onInvalid(errors?.content_ia)}`}
                  >
                    <ArticleEditor
                      initialHtml={article_edit?.content_ia ?? cloneSource?.content_ia}
                      onChange={(value) => {
                        setArticle({ ...article_edit, content_ia: value.html });
                        setValue("content_ia", value.html ?? "", {
                          shouldDirty: true,
                        });
                      }}
                      className={`${onInvalid(errors?.content_ia)}`}
                      register={register}
                      fieldName="content_ia"
                      viewToolbar="compact"
                    />
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          </Col>
          {isPreview ? (
            <Col md={4}>
              <Card className="h-100">
                <Card.Body className="preview">
                  <p>
                    <strong>Visualização</strong>
                  </p>
                  {article?.content || article?.title ? (
                    <>
                      <h2>{article?.title}</h2>
                      <div
                        className="content mt-4"
                        dangerouslySetInnerHTML={{
                          __html: article?.content ?? "",
                        }}
                      ></div>
                    </>
                  ) : (
                    <div className="w-100 d-flex justify-content-center">
                      Não há registro
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ) : (
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <p>
                    <strong>Configurações</strong>
                  </p>
                  <Row>
                    <Col>
                      <FormGroup className="form-group">
                        <label htmlFor="category_id">CATEGORIA *</label>
                        <ReactSelect
                          isSearchable
                          isClearable
                          options={categoriesOptions ?? []}
                          value={selectedCategories}
                          placeholder="Selecione"
                          className={`form-control p-0 ${onInvalid(
                            errors?.category_id,
                          )}`}
                          noOptionsMessage={() => "Não há registros"}
                          styles={customStyles}
                          onChange={(e) => {
                            setSelectedCategories(e);
                          }}
                        />
                        <ErrorMessage
                          name={"Categoria"}
                          type={
                            errors?.category_id?.type?.toString() === "min"
                              ? "required"
                              : errors?.category_id?.type?.toString()
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group">
                        <label htmlFor="section_id">SEÇÃO</label>
                        <ReactSelect
                          isSearchable
                          isClearable
                          options={sectionsOptions ?? []}
                          value={selectedSection}
                          placeholder="Selecione"
                          className={`form-control p-0 ${onInvalid(
                            errors?.section_id,
                          )}`}
                          noOptionsMessage={() => "Não há registros"}
                          styles={customStyles}
                          onChange={(e) => {
                            setSelectedSection(e ?? null);
                          }}
                        />
                        <ErrorMessage
                          name={"Seção"}
                          type={errors?.section_id?.type}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group">
                        <label htmlFor="subsection_id">SUBSEÇÃO</label>
                        <ReactSelect
                          isSearchable
                          isClearable
                          options={subsectionsOptions ?? []}
                          value={selectedSubsection}
                          placeholder="Selecione"
                          className={`form-control p-0 ${onInvalid(
                            errors?.subsection_id,
                          )}`}
                          noOptionsMessage={() => "Não há registros"}
                          styles={customStyles}
                          onChange={(e) => {
                            setSelectedSubsection(e ?? null);
                          }}
                        />
                        <ErrorMessage
                          name={"Subseção"}
                          type={errors?.subsection_id?.type}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group">
                        <label htmlFor="subsection_id">NÍVEIS DE ACESSO</label>
                        <ReactSelect
                          isSearchable
                          isClearable
                          isMulti
                          options={
                            accessProfilesOptions?.filter(
                              (item) => item.value !== 1 && item.value !== 4,
                            ) ?? []
                          }
                          value={selectedAccessProfileArticles}
                          placeholder="Selecione"
                          className={`form-control p-0 ${onInvalid(
                            errors?.access_profile_articles,
                          )}`}
                          noOptionsMessage={() => "Não há registros"}
                          styles={customStyles}
                          onChange={(e) => {
                            setSelectedAccessProfileArticles(
                              e.map((item) => item) || [],
                            );
                          }}
                        />
                        <ErrorMessage
                          name={"Níveis de Acesso"}
                          type={errors?.access_profile_articles?.type}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group">
                        <label htmlFor="related_articles">
                          ARTIGOS RELACIONADOS
                        </label>
                        <ReactSelect
                          isMulti
                          isSearchable
                          isClearable
                          options={articlesRelatedOptions ?? []}
                          value={selectedRelatedArticles}
                          placeholder="Selecione"
                          className={`form-control p-0 ${onInvalid(
                            errors?.related_articles,
                          )}`}
                          noOptionsMessage={() => "Não há registros"}
                          styles={customStyles}
                          onChange={(e) => {
                            setSelectedRelatedArticles(
                              e.map((item) => item) || [],
                            );
                          }}
                        />
                        <ErrorMessage
                          name={"Artigos Relacionados"}
                          type={errors?.related_articles?.type}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group">
                        <label htmlFor="tags">
                          TAGS
                        </label>
                        <ReactSelect
                          isMulti
                          isSearchable
                          isClearable
                          options={tagsOptions ?? []}
                          value={selectedTags}
                          placeholder="Selecione"
                          className={`form-control p-0 ${onInvalid(
                            errors?.tags
                          )}`}
                          noOptionsMessage={() => "Não há registros"}
                          styles={customStyles}
                          onChange={(e) => {
                            setSelectedTags(
                              e.map((item) => item) || []
                            );
                          }}
                        />
                        <ErrorMessage
                          name={"Tags"}
                          type={errors?.tags?.type}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group">
                        <label htmlFor="model_name">MODELO</label>
                        <ReactSelect
                          isSearchable
                          isClearable
                          options={modelNameOptions ?? []}
                          value={selectedModelName}
                          placeholder="Selecione"
                          className={`form-control p-0 ${onInvalid(
                            errors?.model_name,
                          )}`}
                          noOptionsMessage={() => "Não há registros"}
                          styles={customStyles}
                          onChange={(e) => {
                            setSelectedModelName(e);
                          }}
                        />
                        <ErrorMessage
                          name={"Modelo"}
                          type={errors?.model_name?.type?.toString()}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group">
                        {selectedIsFile ? (
                          <>
                            <div
                              className="menu-info"
                              style={{ overflowY: "scroll" }}
                            >
                              {prompts
                                .sort((a, b) => a.id - b.id)
                                .map((prompt, index) => (
                                  <div
                                    className={`d-flex align-items-center ms-2 ${
                                      activePromptId === prompt.id
                                        ? "active-menu"
                                        : ""
                                    }`}
                                  >
                                    <a
                                      key={prompt.id}
                                      onClick={() =>
                                        setActivePromptId(prompt.id)
                                      }
                                      className={`cursor ${
                                        activePromptId === prompt.id
                                          ? ""
                                          : "fw-normal"
                                      }`}
                                    >
                                      PROMPT {index + 1}
                                    </a>

                                    <BvIcon
                                      style={{
                                        cursor: "pointer",
                                        color: "red",
                                      }}
                                      onClick={() => removePrompt(prompt.id)}
                                      name="close"
                                      variant="duo"
                                      weight={600}
                                      width={15}
                                      height={15}
                                    />
                                  </div>
                                ))}

                              <FontAwesomeIcon
                                icon={faCirclePlus}
                                onClick={addPrompt}
                                style={{
                                  cursor: "pointer",
                                  marginBottom: "0",
                                }}
                                className="ms-2"
                              />
                            </div>

                            {activePrompt && (
                              <textarea
                                rows={6}
                                className="form-control"
                                value={activePrompt.value}
                                onChange={(e) =>
                                  handleInformationPrompt(
                                    activePrompt.id,
                                    e.target.value,
                                  )
                                }
                                style={{ resize: "block" }}
                              />
                            )}

                            <ErrorMessage
                              name={"Prompt Artigo"}
                              type={errors?.list_prompt?.type?.toString()}
                              min={3}
                              max={10000}
                            />
                          </>
                        ) : (
                          <>
                            <label htmlFor="prompt_article">
                              <div className="d-flex align-items-center">
                                <span className="me-1">PROMPT ARTIGO</span>
                              </div>
                            </label>

                            <textarea
                              rows={6}
                              id="prompt_article"
                              className={`form-control ${onInvalid(
                                errors?.prompt_article,
                              )}`}
                              {...register("prompt_article")}
                              style={{ resize: "block" }}
                            />
                            <ErrorMessage
                              name={"Prompt Artigo"}
                              type={errors?.prompt_article?.type?.toString()}
                              min={3}
                              max={10000}
                            />
                          </>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group-inline">
                        <label htmlFor="is_internal">ARTIGO INTERNO?</label>

                        <FormCheck className="form-check ms-2" inline>
                          <input
                            className={`form-check-input ${onInvalid(
                              errors?.is_internal,
                            )}`}
                            type="radio"
                            id="caS"
                            value="true"
                            onChange={(e) => {
                              changeIsInternal(true);
                            }}
                            checked={selectedIsInternal}
                          />
                          <label className="form-check-label" htmlFor="caS">
                            Sim
                          </label>
                        </FormCheck>
                        <FormCheck className="form-check" inline>
                          <input
                            className={`form-check-input ${onInvalid(
                              errors?.is_internal,
                            )}`}
                            type="radio"
                            id="caN"
                            value="false"
                            onChange={(e) => {
                              changeIsInternal(false);
                            }}
                            checked={!selectedIsInternal}
                          />
                          <label className="form-check-label" htmlFor="caN">
                            Não
                          </label>
                        </FormCheck>
                        <ErrorMessage
                          name={"Artigo Interno?"}
                          type={errors?.is_internal?.type?.toString()}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="form-group-inline">
                        <label htmlFor="is_file">
                          POSSUI ARQUIVO EXCEL IA?
                        </label>

                        <FormCheck className="form-check ms-2" inline>
                          <input
                            className={`form-check-input ${onInvalid(
                              errors?.is_file,
                            )}`}
                            type="radio"
                            id="cS"
                            value="true"
                            {...register("is_file")}
                            onChange={(e) => {
                              changeIsFile(true);
                            }}
                            checked={selectedIsFile}
                          />
                          <label className="form-check-label" htmlFor="cS">
                            Sim
                          </label>
                        </FormCheck>
                        <FormCheck className="form-check" inline>
                          <input
                            className={`form-check-input ${onInvalid(
                              errors?.is_file,
                            )}`}
                            type="radio"
                            id="cN"
                            value="false"
                            {...register("is_file")}
                            onChange={(e) => {
                              changeIsFile(false);
                            }}
                            checked={!selectedIsFile}
                          />
                          <label className="form-check-label" htmlFor="cN">
                            Não
                          </label>
                        </FormCheck>
                        <ErrorMessage
                          name={"Possui arquivo excel?"}
                          type={errors?.is_file?.type?.toString()}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  {selectedIsFile && (
                    <Row className="mt-2">
                      <Col md={12}>
                        <div className="menu-info">
                          {filesExcel
                            .sort(
                              (a, b) =>
                                getAreaIndex(a.area) - getAreaIndex(b.area),
                            )
                            .map((file, index) => (
                              <div
                                className={`d-flex align-items-center ms-2 ${
                                  activeFileId === file.id ? "active-menu" : ""
                                }`}
                              >
                                <a
                                  key={file.id}
                                  onClick={() => setActiveFileId(file.id)}
                                  className={`cursor ${
                                    activeFileId === file.id ? "" : "fw-normal"
                                  }`}
                                >
                                  ARQUIVO {index + 1}
                                </a>

                                <BvIcon
                                  style={{
                                    cursor: "pointer",
                                    color: "red",
                                  }}
                                  onClick={() => removeFileTab(file.id)}
                                  name="close"
                                  variant="duo"
                                  weight={600}
                                  width={15}
                                  height={15}
                                />
                              </div>
                            ))}

                          <FontAwesomeIcon
                            icon={faCirclePlus}
                            onClick={addFiles}
                            style={{
                              cursor: "pointer",
                              marginBottom: "0",
                            }}
                            className="ms-2"
                          />
                        </div>
                      </Col>
                      <Col md={12}>
                        <div>
                          {fileStatus !== "existing" ? (
                            <InputDropzone
                              label=""
                              name="list_file"
                              files={activeFile?.file ? [activeFile.file] : []}
                              setFiles={(value) => {
                                if (!Array.isArray(value) || value.length === 0)
                                  return;

                                updateActiveFile(value[0]);
                              }}
                              types={fileTypes}
                              maxFiles={1}
                              maxSize={157286400}
                              error={!!errors?.list_file}
                            />
                          ) : activeFile?.filePath ? (
                            <div className="logo-container">
                              <span className="text-center p-2 mt-3">
                                <div className="logo">
                                  <div className="d-flex align-items-center justify-content-center gap-2">
                                    <i className="fas fa-file-excel"></i>
                                    <span className="filename-text">
                                      {`${
                                        getFileParts(activeFile.filePath).name
                                      }.${getFileParts(activeFile.filePath).ext}`}
                                    </span>
                                  </div>
                                </div>
                              </span>
                              <span className="text-end">
                                <button
                                  className="btn btn-sm btn-outline-primary mb-0 mt-3"
                                  onClick={() => {
                                    setEditingFileId(activeFile.id);
                                  }}
                                >
                                  Alterar Arquivo
                                </button>
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <ErrorMessage
                          name={"Arquivo Excel"}
                          type={errors?.list_file?.type?.toString()}
                        />
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    </ModalDefault>
  );
};

export default ModalArticlesInsert;
